import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { listarPedidos, resumoDosPedidos } from '../../../api/previsaoPecas/previsaoPecasApi';
import { AbaPedidos as AbaDePedidos, ListaDePedidos, ListarPedidosParams, PedidoResumo, ResumoDosPedidos } from '../../../interfaces/PrevisaoPecas';
import Paginacao from '../../../components/paginacao/Paginacao';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { notify } from '../../../shared/feedback/notify';
import { CELULA_DE_UMA_LINHA, SITUACAO_DO_PEDIDO, formatarDiaPuro } from '../formatos';

const POR_PAGINA = 20;

const SUB_ABAS: { valor: AbaDePedidos; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'sem_chegada', rotulo: 'Sem chegada' },
  { valor: 'parciais', rotulo: 'Parciais' },
  { valor: 'completos', rotulo: 'Completos' },
  { valor: 'atrasados', rotulo: 'Atrasados' },
  { valor: 'chegaram_hoje', rotulo: 'Chegaram hoje' },
  { valor: 'a_identificar', rotulo: 'A identificar' },
];

interface Props {
  filtros: Omit<ListarPedidosParams, 'aba' | 'pagina' | 'limite'>;
  /** Muda quando algo foi gravado fora daqui (chegada registrada, cancelada): recarrega. */
  versao: number;
  onAbrirPedido: (pedidoId: string) => void;
  estiloDoCard: (comPaginacao: boolean) => React.CSSProperties;
}

/**
 * A aba Pedidos (previsao-pecas/at-02): um pedido por linha, com a situação de chegada derivada do
 * saldo de cada peça. É a tela inicial, porque é pelo pedido que a chegada entra no Nasajon.
 *
 * 🔴 Regra da at-60: falha de carga nunca vira "nenhum pedido" nem cartões zerados — para quem
 * confere chegada, zero é uma afirmação sobre o estoque.
 */
const AbaPedidos: React.FC<Props> = ({ filtros, versao, onAbrirPedido, estiloDoCard }) => {
  const [aba, setAba] = useState<AbaDePedidos>('todos');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<ListaDePedidos | null>(null);
  const [resumo, setResumo] = useState<ResumoDosPedidos | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [carregando, setCarregando] = useState(true);

  // Filtro novo muda o conjunto: sem voltar à página 1, a página 5 do recorte anterior viraria
  // lista vazia sem explicação.
  useEffect(() => setPagina(1), [filtros]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, cartoes] = await Promise.all([
        listarPedidos({ ...filtros, aba, pagina, limite: POR_PAGINA }),
        resumoDosPedidos(filtros),
      ]);
      setDados(lista);
      setResumo(cartoes);
      setFalhou(false);
    } catch (error) {
      setDados(null);
      setResumo(null);
      setFalhou(true);
      notify.errorFrom(error, 'Não foi possível carregar os pedidos. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setCarregando(false);
    }
  }, [filtros, aba, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  const trocarAba = (valor: AbaDePedidos) => {
    setAba(valor);
    setPagina(1);
  };

  const cartoes: { rotulo: string; valor: number | undefined; aba: AbaDePedidos; apoio?: string }[] = [
    { rotulo: 'Chegaram hoje', valor: resumo?.chegaramHoje, aba: 'chegaram_hoje' },
    { rotulo: 'Parciais', valor: resumo?.parciais, aba: 'parciais', apoio: 'Chegou parte das peças' },
    { rotulo: 'Atrasados', valor: resumo?.atrasados, aba: 'atrasados', apoio: 'Previsão vencida sem chegada completa' },
    { rotulo: 'A identificar', valor: resumo?.aIdentificar, aba: 'a_identificar', apoio: 'Item da nota sem peça do pedido' },
    { rotulo: 'Sem chegada', valor: resumo?.semChegada, aba: 'sem_chegada' },
  ];

  const colunas: ColunaTabela<PedidoResumo>[] = useMemo(
    () => [
      {
        titulo: 'Pedido',
        largura: 90,
        larguraMaxima: 110,
        texto: (p) => p.numero ?? 'sem número',
        render: (p) => (
          <button type="button" className="rastreamento-placa-botao" onClick={() => onAbrirPedido(p.id)}>
            {p.numero ?? 'sem número'}
          </button>
        ),
      },
      {
        titulo: 'Fornecedor',
        largura: 200,
        larguraMaxima: 260,
        texto: (p) => p.fornecedorNome ?? 'não identificado',
        render: (p) => <span style={CELULA_DE_UMA_LINHA}>{p.fornecedorNome ?? 'não identificado'}</span>,
      },
      {
        titulo: 'Veículo / oficina',
        largura: 150,
        larguraMaxima: 200,
        texto: (p) => [p.placa, p.oficina].filter(Boolean).join(' - ') || 'não informado',
        render: (p) => (
          <span style={CELULA_DE_UMA_LINHA}>{[p.placa, p.oficina].filter(Boolean).join(' - ') || 'não informado'}</span>
        ),
      },
      {
        titulo: 'Peças',
        largura: 90,
        larguraMaxima: 110,
        // "3 de 5 chegaram" pela contagem de itens com saldo — a quantidade fica no pedido aberto.
        texto: (p) =>
          p.totalItens === undefined
            ? '-'
            : p.totalItens === 0
              ? 'itens não lidos'
              : `${p.totalItens - (p.itensComSaldo ?? 0)} de ${p.totalItens}`,
      },
      {
        titulo: 'Previsão',
        largura: 100,
        larguraMaxima: 100,
        texto: (p) => formatarDiaPuro(p.previsao),
      },
      {
        titulo: 'Última chegada',
        largura: 110,
        larguraMaxima: 110,
        texto: (p) => formatarDiaPuro(p.ultimaChegadaEm, 'nenhuma'),
      },
      {
        titulo: 'Chegada',
        largura: 150,
        larguraMaxima: 170,
        texto: (p) =>
          `${SITUACAO_DO_PEDIDO[p.situacaoChegada].rotulo}${p.itensAIdentificar ? ' · a identificar' : ''}`,
        render: (p) => (
          <span style={{ display: 'inline-flex', gap: 6, whiteSpace: 'nowrap' }}>
            <span className={`cilia-badge ${SITUACAO_DO_PEDIDO[p.situacaoChegada].classe}`}>
              {SITUACAO_DO_PEDIDO[p.situacaoChegada].rotulo}
            </span>
            {p.itensAIdentificar > 0 && <span className="cilia-badge badge-laranja">A identificar</span>}
          </span>
        ),
      },
    ],
    [onAbrirPedido],
  );

  const total = dados?.total;
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / POR_PAGINA)) : 1;
  const temPaginacao = totalPaginas > 1;

  return (
    <>
      {/* Cartões só com consulta respondida: zero por falha seria lido como "nada chegou". */}
      {!falhou && (
        <div className="do-kpis" style={{ width: '100%' }}>
          {cartoes.map((c) => (
            <button
              key={c.rotulo}
              type="button"
              className="do-kpi do-kpi-clicavel"
              title={`Ver os pedidos: ${c.rotulo}`}
              onClick={() => trocarAba(c.aba)}
              style={aba === c.aba ? { borderColor: '#ea5d0e' } : undefined}
            >
              <div className="do-kpi-rotulo">{c.rotulo}</div>
              <div className="do-kpi-valor">{c.valor === undefined ? '...' : c.valor}</div>
              {c.apoio && <div className="do-kpi-apoio">{c.apoio}</div>}
            </button>
          ))}
        </div>
      )}

      <div className="sub-abas">
        {SUB_ABAS.map((a) => (
          <button
            key={a.valor}
            type="button"
            className={`sub-aba ${aba === a.valor ? 'active' : ''}`}
            onClick={() => trocarAba(a.valor)}
          >
            {a.rotulo}
            {aba === a.valor && !falhou && total !== undefined ? ` (${total})` : ''}
          </button>
        ))}
      </div>

      {falhou && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar os pedidos. A ausência de pedidos aqui não significa que não há peça a caminho.
          Recarregue a página; se persistir, acione a TI.
        </div>
      )}

      {!falhou && (
        <div style={estiloDoCard(temPaginacao)}>
          <TabelaSistema
            colunas={colunas}
            itens={dados?.itens ?? []}
            chave={(p) => p.id}
            vazio="Nenhum pedido nesta situação."
            carregando={carregando}
          />
          {temPaginacao && <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />}
        </div>
      )}
    </>
  );
};

export default AbaPedidos;
